import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import FrameList from './FrameList';
import MatList from './MatList';
import Swal from 'sweetalert2';

function FrameYourCanvasArt({ image }) {
    /* ================= IMAGE ================= */
    const [userImage, setUserImage] = useState(() => {
    if (image) return image;

    const stored = localStorage.getItem('opticon_last_image');
    return stored || null;
});
    const fileInputRef = useRef(null);

    /* ================= SIZE ================= */
    const [width, setWidth] = useState(30);
    const [height, setHeight] = useState(30);
    const [imageAspect, setImageAspect] = useState(null);
    const [sizeMode, setSizeMode] = useState('manual');
    const [imagePxSize, setImagePxSize] = useState(null);
    /* ================= IMAGE SIZE DISPLAY ================= */
    const PRINT_DPI = 300;
    const pxToCm = px => ((px / PRINT_DPI) * 2.54).toFixed(1);
    /* ================= FRAME ================= */
    const [frameOpen, setFrameOpen] = useState(false);
    const [selectedFrame, setSelectedFrame] = useState(null);

    /* ================= CANVAS ================= */
    const canvasRef = useRef(null);

    /* ================= LOADER ================= */
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [sizeTouched, setSizeTouched] = useState(false);

    /* ================= IMAGE UPLOAD ================= */
    const handleImageUpload = (e) => {
        if (!e.target.files?.length) return;

        const file = e.target.files[0];

        // ✅ Validate image type
        if (!file.type.startsWith('image/')) return;

        const MAX_MB = 64;

        // ✅ Validate size (64MB limit)
        if (file.size > MAX_MB * 1024 * 1024) {
            Swal.fire({
                icon: 'error',
                title: 'Image too large',
                text: 'Please upload an image under 64MB.',
                confirmButtonText: 'OK'
            });
            return;
        }

        const reader = new FileReader();

        reader.onload = () => {
            const img = new Image();

            img.onload = () => {
                const imgW = img.naturalWidth;
                const imgH = img.naturalHeight;

                setImagePxSize({ w: imgW, h: imgH });

                // AUTO size from image (only initially)
                const aspect = imgW / imgH;
                const defaultW = 30;
                const defaultH = Math.round(defaultW / aspect);

                setWidth(defaultW);
                setHeight(defaultH);
                setSizeTouched(false);

                setUserImage(reader.result);
                localStorage.setItem('opticon_last_image', reader.result);
            };

            img.src = reader.result;
        };

        reader.readAsDataURL(file);
    };

    /* ================= FRAME OPTIONS ================= */
    const frameOptions = FrameList;

    const totalPrice = (selectedFrame?.price || 0);
    /* ================= GST ================= */
    const GST_RATE = 0.1; // 10%
    const gstAmount = totalPrice * GST_RATE;
    const totalWithGST = totalPrice + gstAmount;

    /* ================= CANVAS RENDER ================= */
    const DEFAULT_CANVAS_WIDTH = 520;
    const DEFAULT_CANVAS_HEIGHT = 360;

    const updateCanvasSize = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const PREVIEW_PPCM = 20;
        const PREVIEW_PPPX = 0.15;

        let previewW, previewH;

        if (userImage && imagePxSize?.w && imagePxSize?.h) {
            previewW = imagePxSize.w * PREVIEW_PPPX;
            previewH = imagePxSize.h * PREVIEW_PPPX;
        } else {
            previewW = width * PREVIEW_PPCM;
            previewH = height * PREVIEW_PPCM;
        }

        canvas.width = Math.round(previewW);
        canvas.height = Math.round(previewH);
    };

    const renderCanvas = async () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const W = canvas.width;
        const H = canvas.height;

        ctx.clearRect(0, 0, W, H);

        /* ================= UNITS ================= */
        const PREVIEW_PX_PER_CM = 10;

        // Frame thickness comes from backend in mm → convert to cm → preview px
        const frameTpx =
            ((selectedFrame?.thickness || 0) / 10) * PREVIEW_PX_PER_CM;

        /* ================= SAFETY CLAMP ================= */
        const maxInset = Math.min(W, H) / 2 - 4;
        let insetPx = Math.min(frameTpx, maxInset);

        /* ================= IMAGE ================= */
        if (userImage) {
            const img = new Image();
            img.src = userImage;
            await new Promise(res => (img.onload = res));

            ctx.drawImage(
                img,
                insetPx,
                insetPx,
                W - insetPx * 2,
                H - insetPx * 2
            );
        }

        /* ================= FRAME ================= */
        if (selectedFrame?.img && frameTpx > 0) {
            const frameImg = new Image();
            frameImg.src = selectedFrame.img;
            await new Promise(res => (frameImg.onload = res));

            // Top
            ctx.drawImage(frameImg, 0, 0, W, frameTpx);
            // Bottom
            ctx.drawImage(frameImg, 0, H - frameTpx, W, frameTpx);
            // Left
            ctx.drawImage(frameImg, 0, 0, frameTpx, H);
            // Right
            ctx.drawImage(frameImg, W - frameTpx, 0, frameTpx, H);
        }
    };

    /* ================= ADD TO CART ================= */
    const handleAddToCart = async () => {
        if (!canvasRef.current) return;

        setLoading(true);
        setProgress(0);

        try {
            let imageToSend = '';

            // ✅ If user uploaded image → send that
            if (userImage) {
                imageToSend = userImage;
            }
            // ✅ If no user image → send rendered canvas preview
            else {
                imageToSend = canvasRef.current.toDataURL('image/png');
            }

            const payload = {
                id: 8517,
                quantity: 1,
                meta_data: [
                    { key: 'image_size', value: `${width}x${height}` },
                    { key: 'frame_name', value: selectedFrame?.name || 'No Frame' },
                    { key: 'frame_price', value: totalPrice },
                    { key: 'total_price', value: totalWithGST },
                    { key: 'edited_image', value: imageToSend }
                ]
            };
            console.log('Payload for API:', payload);
            await axios.post(
                'https://opticonframing.com.au/wp-json/opticon/v1/frame-your-canvas-art/add-to-cart',
                payload,
                {
                    headers: { 'Content-Type': 'application/json' },
                    withCredentials: true,
                    onUploadProgress: e => {
                        if (!e.total) return;
                        setProgress(Math.round((e.loaded * 100) / e.total));
                    }
                }
            );

            setProgress(100);

            setTimeout(() => {
                window.location.href = 'https://opticonframing.com.au/cart';
            }, 700);

        } catch (err) {
            console.error(err);
            alert('Add to cart failed');
            setLoading(false);
            setProgress(0);
        }
    };

    const handleRemoveImage = () => {
        setUserImage(null);
        setImageAspect(null);
        setImagePxSize(null); // 🔑 reset image sizing

        setSizeMode('manual');
        setWidth(30);
        setHeight(30);
        setSizeTouched(false);

        localStorage.removeItem('opticon_last_image');
    };

    useEffect(() => {
        (async () => {
            updateCanvasSize();
            renderCanvas();
        })();
    }, [userImage, selectedFrame, width, height]);


    useEffect(() => {
        if (!selectedFrame && frameOptions.length > 0) {
            setSelectedFrame(frameOptions[0]);
        }
    }, [selectedFrame, frameOptions]);

    useEffect(() => {
        if (!userImage || imagePxSize) return;

        const img = new Image();
        img.onload = () => {
            setImageAspect(img.naturalWidth / img.naturalHeight);

            setImagePxSize({
                w: img.naturalWidth,
                h: img.naturalHeight
            });

            setSizeMode('image'); // 🔑 force image-driven sizing
        };

        img.src = userImage;
    }, [userImage, imagePxSize]);

    useEffect(() => {
    if (image) {
        setUserImage(image);
        localStorage.setItem('opticon_last_image', image);
    }
}, [image]);

    /* ================= UI ================= */
    return (
        <div className="product-page">

            {loading && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
                    backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
                    justifyContent: 'center', alignItems: 'center', zIndex: 9999
                }}>
                    {/* Circular Loader */}
                    <div style={{
                        width: '60px', height: '60px', borderRadius: '50%',
                        border: '5px solid rgba(255,255,255,0.1)',
                        borderTop: '5px solid #1E5FFF', // Requested Color
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px'
                    }}></div>

                    {/* Percentage Text */}
                    <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
                        {progress}%
                    </p>

                    <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
                </div>
            )}

            {/* LEFT */}
            <div className="product-image-panel">
                <canvas
                    ref={canvasRef}
                    className="real-canvas-preview"
                />
            </div>

            {/* RIGHT */}
            <div className="product-info-panel">
                <h1 className="product-title">Frame Your Canvas Art</h1>

                {/* IMAGE SIZE */}
                <div className="print-option-row">
                    <label className="form-label">Image Size (cm)</label>

                    <div className="image-size-row">
                        {!userImage && (
                            <>
                                <input
                                    type="number"
                                    value={width}
                                    onChange={e => {
                                        setWidth(Number(e.target.value));
                                        setSizeTouched(true);
                                        setSizeMode('manual'); // 🔑
                                    }}

                                    className="form-select"
                                />

                                <input
                                    type="number"
                                    value={height}
                                    onChange={e => {
                                        setHeight(Number(e.target.value));
                                        setSizeTouched(true);
                                        setSizeMode('manual'); // 🔑
                                    }}
                                    className="form-select"
                                />
                            </>
                        )}

                        {userImage && imagePxSize && (
                            <div className="image-dimension-display">
                                <span>
                                    <strong>
                                        {' '}
                                        {pxToCm(imagePxSize.w)}×{pxToCm(imagePxSize.h)} cm
                                    </strong>
                                </span>
                            </div>
                        )}

                        <button
                            className="btn-secondary image-upload-btn"
                            onClick={() => fileInputRef.current.click()}
                        >
                            {userImage ? 'Change Image' : 'Add Image'}
                        </button>

                        {userImage && (
                            <button
                                className="btn-danger image-remove-btn"
                                onClick={handleRemoveImage}
                                type="button"
                            >
                                Remove Image
                            </button>
                        )}

                        <input
                            type="file"
                            ref={fileInputRef}
                            accept="image/*"
                            hidden
                            onChange={handleImageUpload}
                        />
                    </div>
                </div>


                {/* FRAME */}
                <div
                    className="print-option-row frame-row"
                >
                    <label className="form-label">Frame</label>
                    <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
                        <span>{selectedFrame?.name || 'Select Frame'}</span>
                        <span className="frame-arrow">›</span>
                    </div>
                </div>

                {/* PRICE */}
                <div className="price-cart-row">
                    <div className="product-price">${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)}= ${totalWithGST.toFixed(2)}</div>

                    <button
                        className="add-to-cart-btn"
                        onClick={handleAddToCart}
                        disabled={loading}
                    >
                        {loading ? `${progress}%` : 'Add to Cart'}
                    </button>
                </div>
            </div>

            {/* FRAME SIDEBAR */}
            {frameOpen && (
                <div className="frame-sidebar open">
                    <div className="frame-sidebar-header">
                        <h3>Frames</h3>
                        <button onClick={() => setFrameOpen(false)}>✕</button>
                    </div>

                    {frameOptions.map(frame => (
                        <div
                            key={frame.id}
                            className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
                            onClick={() => {
                                setSelectedFrame(frame);
                                setFrameOpen(false);
                            }}
                        >
                            <img src={frame.img} alt={frame.name} />
                            <div className="frame-meta">
                                <div className="frame-name">{frame.name}</div>
                                <div className="frame-price">+ ${frame.price}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
}

export default FrameYourCanvasArt;